import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Image as ImageIcon,
  ArrowLeft,
  Loader2,
  User,
  Settings,
  Layers,
  Key,
  Gift,
  X,
  HelpCircle,
  Megaphone,
  Sparkles,
  FileText,
  History,
  BarChart3,
  CreditCard,
  Coins,
  Wallet,
  Package,
  ShoppingCart,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  PieChart,
  FolderOpen,
  Bot,
  MessageSquare,
  Menu,

  Flag,
  HandCoins,
  Shield,
  Wand2,
  Film,
  Clapperboard,
  HardDrive,
  Plug,
  LayoutGrid,
  ShoppingBag,
  Presentation,
  DollarSign,
  Database,
  Palette,
} from 'lucide-react';
import { api, faqAPI, type FAQ, announcementAPI, type Announcement, apiChannelAPI, type ApiChannel, type ChannelHealth, promptOptimizeAPI, type PromptOptimizeConfig, templateAPI, type PromptTemplateDetail, paymentAdminAPI, type PaymentOrderAdmin, type PaginationInfo, invitationAPI, type AdminInvitationRecord, type ManagedApplication } from '../services/api';
import toast from '../components/Toast';
import AdminModelForm from '../components/admin/ModelForm';
import type { UserData, Stats, ImageJob, SystemConfig, RedeemCode, Redemption, ModelConfigData, SmsRecord } from '../components/admin/types';
import DashboardTab from '../components/admin/DashboardTab';
import GenerationStatsTab from '../components/admin/GenerationStatsTab';
import FreeCreditStatsTab from '../components/admin/FreeCreditStatsTab';
import UsersTab from '../components/admin/UsersTab';
import BlacklistTab from '../components/admin/BlacklistTab';
import ImagesTab from '../components/admin/ImagesTab';
import RedeemCodesTab from '../components/admin/RedeemCodesTab';
import TemplatesTab from '../components/admin/TemplatesTab';
import SettingsTab from '../components/admin/SettingsTab';
import ApplicationsTab from '../components/admin/ApplicationsTab';
import ModelsTab from '../components/admin/ModelsTab';
import FAQTab from '../components/admin/FAQTab';
import AnnouncementsTab from '../components/admin/AnnouncementsTab';
import ApiChannelsTab from '../components/admin/ApiChannelsTab';
import UpstreamBalancesTab from '../components/admin/UpstreamBalancesTab';
import PromptOptimizeTab from '../components/admin/PromptOptimizeTab';
import PaymentConfigTab from '../components/admin/PaymentConfigTab';
import RechargePackagesTab from '../components/admin/RechargePackagesTab';
import LlmTrafficPackagesTab from '../components/admin/LlmTrafficPackagesTab';
import OrdersTab from '../components/admin/OrdersTab';
import OrderStatsTab from '../components/admin/OrderStatsTab';
import SmsRecordsTab from '../components/admin/SmsRecordsTab';
import ReportedWorksTab from '../components/admin/ReportedWorksTab';
import OAuthConfigTab from '../components/admin/OAuthConfigTab';
import VideoChannelsTab from '../baselines/video/admin/VideoChannelsTab';
import VideoProvidersTab from '../baselines/video/admin/VideoProvidersTab';
import ImageProvidersTab from '../components/admin/ImageProvidersTab';
import VideoModelsTab from '../baselines/video/admin/VideoModelsTab';
import VideoJobsTab from '../baselines/video/admin/VideoJobsTab';
import StorageConfigTab from '../baselines/video/admin/StorageConfigTab';
import VideoSystemConfigTab from '../baselines/video/admin/VideoSystemConfigTab';

import DirectorDataHubTab from '../components/admin/director/DirectorDataHubTab';
import DirectorAIConfigTab from '../components/admin/director/DirectorAIConfigTab';
import DirectorAngleTemplatesHubTab from '../components/admin/director/DirectorAngleTemplatesHubTab';
import DirectorBasicConfigTab from '../components/admin/director/DirectorBasicConfigTab';
import LlmPlatformTab from '../components/admin/llm/LlmPlatformTab';
import CanvasProjectsTab from '../components/admin/canvas/CanvasProjectsTab';
import CanvasAIConfigTab from '../components/admin/canvas/CanvasAIConfigTab';
import CanvasStatsTab from '../components/admin/canvas/CanvasStatsTab';
import CanvasStyleCardsTab from '../components/admin/canvas/CanvasStyleCardsTab';
import EcommerceTasksTab from '../components/admin/ecommerce/EcommerceTasksTab';
import EcommerceStatsTab from '../components/admin/ecommerce/EcommerceStatsTab';
import EcommerceSuiteConfigTab from '../components/admin/ecommerceSuite/EcommerceSuiteConfigTab';
import EcommerceSuiteProjectsTab from '../components/admin/ecommerceSuite/EcommerceSuiteProjectsTab';
import EcommerceSuiteStatsTab from '../components/admin/ecommerceSuite/EcommerceSuiteStatsTab';
import EcommerceSuiteTemplatesTab from '../components/admin/ecommerceSuite/EcommerceSuiteTemplatesTab';
import PptProjectsTab from '../components/admin/ppt/PptProjectsTab';
import InvitationsTab from '../components/admin/InvitationsTab';
import ReferralRebateTab from '../components/admin/ReferralRebateTab';
import FailureLogsTab from '../components/admin/FailureLogsTab';
import ModelFallbackStatsTab from '../components/admin/ModelFallbackStatsTab';
import ErrorRulesTab from '../components/admin/ErrorRulesTab';
import ImageConfigTab from '../components/admin/ImageConfigTab';
import StorageCleanupTab from '../components/admin/StorageCleanupTab';
import AppReleaseTab from '../components/admin/AppReleaseTab';
import StorageBucketTab from '../components/admin/StorageBucketTab';
import DatabaseSchemaTab from '../components/admin/DatabaseSchemaTab';
import UserMilestonesTab from '../components/admin/UserMilestonesTab';
import RevenueMilestonesTab from '../components/admin/RevenueMilestonesTab';
import ModelRoutingForm from '../components/admin/ModelRoutingForm';
import AdminApiChannelForm from '../components/admin/ApiChannelForm';
import AdminFAQForm from '../components/admin/FAQForm';
import AdminAnnouncementForm from '../components/admin/AnnouncementForm';
import UserEditForm from '../components/admin/UserEditForm';
import UserCreditsForm from '../components/admin/UserCreditsForm';
import ResetPasswordForm from '../components/admin/ResetPasswordForm';
import { formatBeijingDateTime } from '../utils/time';
import { platformFetch } from '../platform/http';

type ActiveTab = 'dashboard' | 'generationStats' | 'modelFallbackStats' | 'freeCreditStats' | 'userMilestones' | 'revenueMilestones' | 'users' | 'smsRecords' | 'images' | 'failureLogs' | 'errorRules' | 'imageConfig' | 'storageCleanup' | 'storageBucket' | 'dbSchema' | 'invitations' | 'referralRebate' | 'settings' | 'applications' | 'redeemCodes' | 'models' | 'faq' | 'announcements' | 'apiChannels' | 'upstreamBalances' | 'imageProviders' | 'promptOptimize' | 'templates' | 'paymentConfig' | 'rechargePackages' | 'llmTrafficPackages' | 'orders' | 'orderStats' | 'reportedWorks' | 'oauthConfig' | 'videoChannels' | 'videoProviders' | 'videoModels' | 'videoJobs' | 'storageConfig' | 'videoSystemConfig' | 'directorDataHub' | 'directorAIConfig' | 'directorAngleTemplates' | 'directorBasicConfig' | 'llmPlatform' | 'canvasProjects' | 'canvasStats' | 'canvasAIConfig' | 'canvasStyleCards' | 'ecommerceTasks' | 'ecommerceStats' | 'ecommerceSuiteConfig' | 'ecommerceSuiteProjects' | 'ecommerceSuiteStats' | 'ecommerceSuiteTemplates' | 'pptProjects' | 'appRelease' | 'blacklist';

/** 所有合法 tab 值集合，用于 hash 解析时校验 */
const VALID_TABS = new Set<string>([
  'dashboard', 'generationStats', 'modelFallbackStats', 'freeCreditStats', 'userMilestones', 'revenueMilestones', 'users', 'smsRecords', 'images', 'failureLogs', 'errorRules', 'imageConfig',
  'storageCleanup', 'storageBucket', 'dbSchema',
  'invitations', 'referralRebate', 'settings', 'applications', 'redeemCodes', 'models', 'faq', 'announcements',
  'apiChannels', 'upstreamBalances', 'imageProviders', 'promptOptimize', 'templates',
  'paymentConfig', 'rechargePackages', 'llmTrafficPackages', 'orders', 'orderStats', 'reportedWorks',
  'oauthConfig', 'videoChannels', 'videoProviders', 'videoModels', 'videoJobs',
  'storageConfig', 'videoSystemConfig', 'directorDataHub', 'directorAIConfig',
  'directorAngleTemplates', 'directorBasicConfig', 'llmPlatform',
  'canvasProjects', 'canvasStats', 'canvasAIConfig', 'canvasStyleCards', 'ecommerceTasks', 'ecommerceStats', 'ecommerceSuiteConfig', 'ecommerceSuiteProjects', 'ecommerceSuiteStats', 'ecommerceSuiteTemplates', 'pptProjects',
  'appRelease',
  'blacklist',
]);

/** 从 URL hash 解析初始 tab，无效则返回默认值 */
function getInitialTab(): ActiveTab {
  const hash = window.location.hash.replace('#', '');
  return VALID_TABS.has(hash) ? (hash as ActiveTab) : 'dashboard';
}

export default function Admin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActiveTab>(getInitialTab);

  // 切换 tab 时同步到 URL hash，刷新后能恢复到上次页面
  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<UserData[]>([]);
  const [usersPagination, setUsersPagination] = useState<PaginationInfo>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [images, setImages] = useState<ImageJob[]>([]);
  const [adminInvitations, setAdminInvitations] = useState<AdminInvitationRecord[]>([]);
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [applications, setApplications] = useState<ManagedApplication[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [redeemCodes, setRedeemCodes] = useState<RedeemCode[]>([]);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [usersSearchQuery, setUsersSearchQuery] = useState('');
  const [usersMultiIpOnly, setUsersMultiIpOnly] = useState(false);
  const [usersMinSameIp, setUsersMinSameIp] = useState(2);
  const [usersMultiLoginIpOnly, setUsersMultiLoginIpOnly] = useState(false);
  const [usersMinSameLoginIp, setUsersMinSameLoginIp] = useState(2);
  const [usersSameFingerprintOnly, setUsersSameFingerprintOnly] = useState(false);
  const [currentUser, setCurrentUser] = useState<{ email: string; role?: string } | null>(null);

  const [configForm, setConfigForm] = useState({
    siteName: '',
    siteUrl: '',
    signupCredits: 2,
    directorEnabled: true,
    canvasEnabled: true,
    ecommerceReplicateEnabled: true,
    pptEnabled: true,
    videoEnabled: true,
    photoRestoreEnabled: true,
    inviteEnabled: false,
    inviterCredits: 0,
    inviteeCredits: 0,
    invitePhoneOnly: false,
    smtpHost: '',
    smtpPort: 465,
    smtpSecure: true,
    smtpUser: '',
    smtpPass: '',
    smtpFrom: '',
    smtpFromName: 'AI图片生成',
    emailProvider: 'smtp' as 'smtp' | 'resend',
    resendApiKey: '',
    resendFromEmail: '',
    resendFromName: 'AI图片生成',
    verifyCodeTemplate: '',
    errorAlertEnabled: false,
    errorAlertWindowMinutes: 5,
    errorAlertThreshold: 10,
    errorAlertRecipient: '',
    checkinEnabled: false,
    checkinMinCredits: 1,
    checkinMaxCredits: 10,
    heroTitle: '',
    heroSubtitle: '',
    heroFeature1: '',
    heroFeature2: '',
    heroFeature3: '',
    referenceImageMaxCount: 16,
    defaultModelId: '',
    emailRegisterEnabled: true,
    phoneRegisterEnabled: true,
    allowedEmailDomains: '',
    ipRegisterDailyLimit: 1,
    emailCodeIpHourlyLimit: 3,
    emailCodeIpDailyLimit: 5,
    captchaEnabled: true,
    emailCodePerEmailHourlyLimit: 3,
    emailCodePerEmailDailyLimit: 5,
    announcementAutoCollapseDelaySec: 5,
    analyticsTrackingCode: '',
  });
  const [configSaved, setConfigSaved] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  const [models, setModels] = useState<ModelConfigData[]>([]);
  const [editingModel, setEditingModel] = useState<ModelConfigData | null>(null);
  const [showModelForm, setShowModelForm] = useState(false);
  const [routingModel, setRoutingModel] = useState<ModelConfigData | null>(null);
  const [showRoutingForm, setShowRoutingForm] = useState(false);
  const [movingModelId, setMovingModelId] = useState<string | null>(null);

  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);
  const [showFAQForm, setShowFAQForm] = useState(false);

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);

  const [previewImage, setPreviewImage] = useState<ImageJob | null>(null);

  const [apiChannels, setApiChannels] = useState<ApiChannel[]>([]);
  const [channelHealth, setChannelHealth] = useState<Record<string, ChannelHealth>>({});
  const [editingChannel, setEditingChannel] = useState<ApiChannel | null>(null);
  const [showChannelForm, setShowChannelForm] = useState(false);

  const [, setPromptOptimizeConfig] = useState<PromptOptimizeConfig | null>(null);
  const [promptOptimizeForm, setPromptOptimizeForm] = useState({
    isEnabled: false,
    model: '',
    systemPromptText2img: '',
    systemPromptImg2img: '',
    systemPromptInpaint: '',
    mergePromptTemplate: '',
    temperature: 0.7,
    maxTokens: 500,
  });
  const [promptOptimizeSaved, setPromptOptimizeSaved] = useState(false);

  const [templates, setTemplates] = useState<(PromptTemplateDetail & { _count: { favorites: number; usages: number } })[]>([]);

  const [createForm, setCreateForm] = useState({
    credits: 10,
    count: 1,
    maxUses: 1,
    expiresAt: '',
    prefix: '',
  });
  const [createdCodes, setCreatedCodes] = useState<RedeemCode[]>([]);

  const [creditHistoryModal, setCreditHistoryModal] = useState<{
    isOpen: boolean;
    userId: string;
    userName: string;
    userEmail: string;
    currentCredits: number;
    transactions: Array<{
      id: string;
      amount: number;
      type: string;
      description: string;
      createdAt: string;
    }>;
  } | null>(null);

  const [creditAdjustUser, setCreditAdjustUser] = useState<UserData | null>(null);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<UserData | null>(null);

  // 订单管理状态
  const [orders, setOrders] = useState<PaymentOrderAdmin[]>([]);
  const [orderPagination, setOrderPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // 图片管理状态
  const [imagePagination, setImagePagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [imageFilters, setImageFilters] = useState({
    userEmail: '',
    status: '',
    model: '',
    source: '',
    mode: '',
    startDate: '',
    endDate: '',
    search: '',
  });
  const [orderStats, setOrderStats] = useState({
    totalAmount: 0,
    totalCredits: 0,
    totalOrders: 0,
  });
  const [orderSearchParams, setOrderSearchParams] = useState<{
    search?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }>({});
  const [smsRecords, setSmsRecords] = useState<SmsRecord[]>([]);
  const [smsRecordsLoading, setSmsRecordsLoading] = useState(false);
  const [smsRecordsPagination, setSmsRecordsPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [smsFilters, setSmsFilters] = useState<{ phone?: string; status?: string; type?: string }>({});

  // 移动端侧边栏状态
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    checkAdmin();
  }, []);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadData();
    }
  }, [activeTab, currentUser, usersMultiIpOnly, usersMinSameIp, usersMultiLoginIpOnly, usersMinSameLoginIp, usersSameFingerprintOnly, usersPagination.page, usersPagination.limit, usersSearchQuery]);

  const checkAdmin = async () => {
    try {
      const user = await api.getMe();
      if (user.role !== 'admin') {
        navigate('/');
        return;
      }
      setCurrentUser(user);
    } catch {
      navigate('/');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const res = await platformFetch('/admin/stats');
        if (res.ok) setStats(await res.json());
      } else if (activeTab === 'users') {
          const qs = new URLSearchParams({
            page: String(usersPagination.page),
            limit: String(usersPagination.limit),
          });
          if (usersSearchQuery.trim()) {
            qs.set('search', usersSearchQuery.trim());
          }
        if (usersMultiIpOnly) {
          qs.set('multiIpOnly', '1');
          qs.set('minSameIp', String(usersMinSameIp));
        }
        if (usersMultiLoginIpOnly) {
          qs.set('multiLoginIpOnly', '1');
          qs.set('minSameLoginIp', String(usersMinSameLoginIp));
        }
        if (usersSameFingerprintOnly) {
          qs.set('sameFingerprintOnly', '1');
        }
        const url = `/admin/users${qs.toString() ? `?${qs.toString()}` : ''}`;
        const res = await platformFetch(url);
        if (res.ok) {
          const data = await res.json();
          setUsers(data.data);
            setUsersPagination((prev) => ({
              page: data.pagination?.page ?? prev.page,
              limit: data.pagination?.limit ?? prev.limit,
              total: data.pagination?.total ?? 0,
              totalPages: data.pagination?.totalPages ?? 0,
            }));
        }
      } else if (activeTab === 'smsRecords') {
        await loadSmsRecords(1, smsFilters);
      } else if (activeTab === 'images') {
        await loadImages();
      } else if (activeTab === 'invitations') {
        const res = await invitationAPI.getAdminInvitations();
        setAdminInvitations(res.data);
      } else if (activeTab === 'applications') {
        setApplicationsLoading(true);
        try {
          const res = await api.getAdminApplications();
          setApplications(res.data);
        } catch {
          setApplications([]);
        } finally {
          setApplicationsLoading(false);
        }
      } else if (activeTab === 'settings') {
        const [configRes, modelsRes] = await Promise.all([
          platformFetch('/config'),
          platformFetch('/models'),
        ]);
        if (modelsRes.ok) {
          const modelsData = await modelsRes.json();
          setModels(modelsData.data);
        }
        if (configRes.ok) {
          const data = await configRes.json();
          setConfig(data);
          setConfigForm({
            siteName: data.siteName || 'AI 图片生成',
            siteUrl: data.siteUrl || '',
            signupCredits: data.signupCredits ?? 2,
            directorEnabled: data.directorEnabled ?? true,
            canvasEnabled: data.canvasEnabled ?? true,
            ecommerceReplicateEnabled: data.ecommerceReplicateEnabled ?? true,
            pptEnabled: data.pptEnabled ?? true,
            videoEnabled: data.videoEnabled ?? true,
            photoRestoreEnabled: data.photoRestoreEnabled ?? true,
            inviteEnabled: data.inviteEnabled ?? false,
            inviterCredits: data.inviterCredits ?? 0,
            inviteeCredits: data.inviteeCredits ?? 0,
            invitePhoneOnly: data.invitePhoneOnly ?? false,
            smtpHost: data.smtpHost || '',
            smtpPort: data.smtpPort || 465,
            smtpSecure: data.smtpSecure ?? true,
            smtpUser: data.smtpUser || '',
            smtpPass: data.smtpPass || '',
            smtpFrom: data.smtpFrom || '',
            smtpFromName: data.smtpFromName || 'AI图片生成',
            emailProvider: data.emailProvider || 'smtp',
            resendApiKey: data.resendApiKey || '',
            resendFromEmail: data.resendFromEmail || '',
            resendFromName: data.resendFromName || 'AI图片生成',
            verifyCodeTemplate: data.verifyCodeTemplate || '',
            errorAlertEnabled: data.errorAlertEnabled ?? false,
            errorAlertWindowMinutes: data.errorAlertWindowMinutes ?? 5,
            errorAlertThreshold: data.errorAlertThreshold ?? 10,
            errorAlertRecipient: data.errorAlertRecipient || '',
            checkinEnabled: data.checkinEnabled ?? false,
            checkinMinCredits: data.checkinMinCredits ?? 1,
            checkinMaxCredits: data.checkinMaxCredits ?? 10,
            heroTitle: data.heroTitle || '',
            heroSubtitle: data.heroSubtitle || '',
            heroFeature1: data.heroFeature1 || '',
            heroFeature2: data.heroFeature2 || '',
            heroFeature3: data.heroFeature3 || '',
            referenceImageMaxCount: data.referenceImageMaxCount ?? 16,
            defaultModelId: data.defaultModelId || '',
            emailRegisterEnabled: data.emailRegisterEnabled ?? true,
            phoneRegisterEnabled: data.phoneRegisterEnabled ?? true,
            allowedEmailDomains: data.allowedEmailDomains ?? '',
            ipRegisterDailyLimit: data.ipRegisterDailyLimit ?? 1,
            emailCodeIpHourlyLimit: data.emailCodeIpHourlyLimit ?? 3,
            emailCodeIpDailyLimit: data.emailCodeIpDailyLimit ?? 5,
            captchaEnabled: data.captchaEnabled ?? true,
            emailCodePerEmailHourlyLimit: data.emailCodePerEmailHourlyLimit ?? 3,
            emailCodePerEmailDailyLimit: data.emailCodePerEmailDailyLimit ?? 5,
            announcementAutoCollapseDelaySec: Math.round((data.announcementAutoCollapseDelayMs ?? 5000) / 1000),
            analyticsTrackingCode: data.analyticsTrackingCode || '',
          });
        }
      } else if (activeTab === 'models') {
        const res = await platformFetch('/models');
        if (res.ok) {
          const data = await res.json();
          setModels(data.data);
        }
      } else if (activeTab === 'faq') {
        const res = await faqAPI.getAdminFAQs();
        setFaqs(res.data);
      } else if (activeTab === 'announcements') {
        const res = await announcementAPI.getAdminAnnouncements();
        setAnnouncements(res.data);
      } else if (activeTab === 'apiChannels') {
        const [res, healthRes] = await Promise.all([
          apiChannelAPI.getChannels(),
          apiChannelAPI.getChannelHealth().catch(() => null),
        ]);
        setApiChannels(res.data);
        if (healthRes) setChannelHealth(healthRes.data);
      } else if (activeTab === 'promptOptimize') {
        const res = await promptOptimizeAPI.getConfig();
        setPromptOptimizeConfig(res);
        setPromptOptimizeForm({
          isEnabled: res.isEnabled,
          model: res.model || '',
          systemPromptText2img: res.systemPromptText2img || '',
          systemPromptImg2img: res.systemPromptImg2img || '',
          systemPromptInpaint: res.systemPromptInpaint || '',
          mergePromptTemplate: (res as any).mergePromptTemplate || '',
          temperature: res.temperature,
          maxTokens: res.maxTokens,
        });
      } else if (activeTab === 'templates') {
        const res = await templateAPI.getAdminTemplates();
        setTemplates(res.data);
      } else if (activeTab === 'redeemCodes') {
        const [codesRes, redemptionsRes] = await Promise.all([
          platformFetch('/credits/codes'),
          platformFetch('/credits/redemptions'),
        ]);
        if (codesRes.ok) {
          const data = await codesRes.json();
          setRedeemCodes(data.data);
        }
        if (redemptionsRes.ok) {
          const data = await redemptionsRes.json();
          setRedemptions(data.data);
        }
      } else if (activeTab === 'orders') {
        await loadOrders();
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    }
    setLoading(false);
  };

  const loadOrders = async (page = 1, search = orderSearchParams) => {
    setLoading(true);
    try {
      const res = await paymentAdminAPI.getOrders({
        page,
        limit: 20,
        ...search,
      });
      setOrders(res.orders);
      setOrderPagination(res.pagination);
      setOrderStats(res.stats);
    } catch (error) {
      console.error('加载订单失败:', error);
    }
    setLoading(false);
  };

  const handleOrderSearch = (params: {
    search?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    setOrderSearchParams(params);
    loadOrders(1, params);
  };

  const handleViewUserRechargeOrders = (user: UserData) => {
    const params = { search: user.id };
    setOrderSearchParams(params);
    setActiveTab('orders');
    loadOrders(1, params);
  };

  const handleOrderPageChange = (page: number) => {
    loadOrders(page, orderSearchParams);
  };

  const handleUserSearchChange = (query: string) => {
    setUsersSearchQuery(query);
    setUsersPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleUsersMultiIpOnlyChange = (value: boolean) => {
    setUsersMultiIpOnly(value);
    setUsersPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleUsersMinSameIpChange = (value: number) => {
    setUsersMinSameIp(value);
    setUsersPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleUsersMultiLoginIpOnlyChange = (value: boolean) => {
    setUsersMultiLoginIpOnly(value);
    setUsersPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleUsersMinSameLoginIpChange = (value: number) => {
    setUsersMinSameLoginIp(value);
    setUsersPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleUsersSameFingerprintOnlyChange = (value: boolean) => {
    setUsersSameFingerprintOnly(value);
    setUsersPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleUserPageChange = (page: number) => {
    setUsersPagination((prev) => ({ ...prev, page }));
  };

  const loadSmsRecords = async (page = 1, filters = smsFilters) => {
    setSmsRecordsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (filters.phone) params.set('phone', filters.phone);
      if (filters.status) params.set('status', filters.status);
      if (filters.type) params.set('type', filters.type);
      const res = await platformFetch(`/admin/sms-records?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setSmsRecords(data.data);
        setSmsRecordsPagination(data.pagination);
      }
    } catch (error) {
      console.error('加载短信记录失败:', error);
    }
    setSmsRecordsLoading(false);
  };

  const handleSmsSearch = (filters: { phone?: string; status?: string; type?: string }) => {
    setSmsFilters(filters);
    loadSmsRecords(1, filters);
  };

  const handleSmsPageChange = (page: number) => {
    loadSmsRecords(page, smsFilters);
  };

  const handleQuerySmsRecord = async (id: string) => {
    const res = await platformFetch(`/admin/sms-records/${id}/query`, {
      method: 'POST',
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || '查询失败');
    }
    toast.success('查询完成');
    await loadSmsRecords(smsRecordsPagination.page, smsFilters);
  };

  const loadImages = async (page = 1, filtersToUse = imageFilters) => {
    setLoading(true);
    try {
      const res = await api.getAdminImageList({
        page,
        limit: 20,
        ...filtersToUse,
      });
      setImages(res.data);
      setImagePagination(res.pagination);
    } catch (error) {
      console.error('加载图片列表失败:', error);
    }
    setLoading(false);
  };

  const handleImagePageChange = (page: number) => {
    loadImages(page);
  };

  const handleImageFilterChange = (newFilters: typeof imageFilters) => {
    setImageFilters(newFilters);
  };

  const handleApplyImageFilters = () => {
    loadImages(1, imageFilters);
  };

  const handleResetImageFilters = () => {
    const resetFilters = {
      userEmail: '',
      status: '',
      model: '',
      source: '',
      mode: '',
      startDate: '',
      endDate: '',
      search: '',
    };
    setImageFilters(resetFilters);
    loadImages(1, resetFilters);
  };

  const handleDeleteImages = async (ids: string[]) => {
    try {
      const result = await api.permanentlyDeleteImages(ids);
      toast.success(result.message);
      loadImages(imagePagination.page);
    } catch (error) {
      toast.error('删除失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  const handleCompleteOrder = async (id: string) => {
    try {
      await paymentAdminAPI.completeOrder(id);
      toast.success('补单成功');
    } catch (error) {
      toast.error('补单失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  const handleRefundOrder = async (id: string, amount?: number, reason?: string) => {
    try {
      await paymentAdminAPI.refundOrder(id, { refundAmount: amount, reason });
      toast.success('退款成功');
    } catch (error) {
      toast.error('退款失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  const handleToggleUser = async (userId: string) => {
    try {
      const res = await platformFetch(`/admin/users/${userId}/toggle`, {
        method: 'POST',
      });
      if (res.ok) loadData();
    } catch {
      toast.error('操作失败');
    }
  };

  const handleViewCreditHistory = async (user: UserData) => {
    try {
      const res = await platformFetch(`/admin/users/${user.id}/credits`);
      if (res.ok) {
        const data = await res.json();
        setCreditHistoryModal({
          isOpen: true,
          userId: user.id,
          userName: user.name || '未设置昵称',
          userEmail: user.email,
          currentCredits: data.user.credits,
          transactions: data.data,
        });
      }
    } catch {
      toast.error('获取积分记录失败');
    }
  };

  const handleImpersonateUser = async (user: UserData) => {
    const confirmed = window.confirm(
      `将在新标签页中以 ${user.email} 的身份登录。\n\n该会话不会修改该用户最后登录时间/IP，也不会影响该用户当前登录状态。是否继续？`,
    );

    if (!confirmed) return;

    const targetWindow = window.open('/impersonate', '_blank');
    if (!targetWindow) {
      toast.error('新标签页被浏览器拦截，请允许弹窗后重试');
      return;
    }

    try {
      const data = await api.createUserImpersonationSession(user.id);
      targetWindow.name = JSON.stringify({
        type: 'IMPERSONATION_SESSION',
        token: data.token,
        user: data.user,
      });
      targetWindow.focus();
      toast.success('已在新标签页打开代登录会话');
    } catch (error) {
      targetWindow.close();
      toast.error(error instanceof Error ? error.message : '代登录失败');
    }
  };

  const handleSaveConfig = async () => {
    try {
      const res = await platformFetch('/config', {
        method: 'POST',
        body: JSON.stringify({
          siteName: configForm.siteName,
          siteUrl: configForm.siteUrl,
          signupCredits: configForm.signupCredits,
          // 应用开关由“应用管理”专用接口维护，通用配置保存不再提交，避免覆盖
          videoEnabled: configForm.videoEnabled,
          inviteEnabled: configForm.inviteEnabled,
          inviterCredits: configForm.inviterCredits,
          inviteeCredits: configForm.inviteeCredits,
          invitePhoneOnly: configForm.invitePhoneOnly,
          smtpHost: configForm.smtpHost,
          smtpPort: configForm.smtpPort,
          smtpSecure: configForm.smtpSecure,
          smtpUser: configForm.smtpUser,
          smtpPass: configForm.smtpPass,
          smtpFrom: configForm.smtpFrom,
          smtpFromName: configForm.smtpFromName,
          emailProvider: configForm.emailProvider,
          resendApiKey: configForm.resendApiKey,
          resendFromEmail: configForm.resendFromEmail,
          resendFromName: configForm.resendFromName,
          verifyCodeTemplate: configForm.verifyCodeTemplate,
          errorAlertEnabled: configForm.errorAlertEnabled,
          errorAlertWindowMinutes: configForm.errorAlertWindowMinutes,
          errorAlertThreshold: configForm.errorAlertThreshold,
          errorAlertRecipient: configForm.errorAlertRecipient,
          checkinEnabled: configForm.checkinEnabled,
          checkinMinCredits: configForm.checkinMinCredits,
          checkinMaxCredits: configForm.checkinMaxCredits,
          heroTitle: configForm.heroTitle,
          heroSubtitle: configForm.heroSubtitle,
          heroFeature1: configForm.heroFeature1,
          heroFeature2: configForm.heroFeature2,
          heroFeature3: configForm.heroFeature3,
          referenceImageMaxCount: configForm.referenceImageMaxCount,
          defaultModelId: configForm.defaultModelId,
          emailRegisterEnabled: configForm.emailRegisterEnabled,
          phoneRegisterEnabled: configForm.phoneRegisterEnabled,
          allowedEmailDomains: configForm.allowedEmailDomains,
          ipRegisterDailyLimit: configForm.ipRegisterDailyLimit,
          emailCodeIpHourlyLimit: configForm.emailCodeIpHourlyLimit,
          emailCodeIpDailyLimit: configForm.emailCodeIpDailyLimit,
          captchaEnabled: configForm.captchaEnabled,
          emailCodePerEmailHourlyLimit: configForm.emailCodePerEmailHourlyLimit,
          emailCodePerEmailDailyLimit: configForm.emailCodePerEmailDailyLimit,
          announcementAutoCollapseDelayMs: configForm.announcementAutoCollapseDelaySec * 1000,
          analyticsTrackingCode: configForm.analyticsTrackingCode,
        })
      });
      if (res.ok) {
        setConfigSaved(true);
        setTimeout(() => setConfigSaved(false), 2000);
        loadData();
      }
    } catch {
      toast.error('保存失败');
    }
  };

  const handleTestSMTP = async () => {
    setSmtpTesting(true);
    try {
      const res = await platformFetch('/config/smtp/test', {
        method: 'POST',
        body: JSON.stringify({
          host: configForm.smtpHost,
          port: configForm.smtpPort,
          secure: configForm.smtpSecure,
          user: configForm.smtpUser,
          pass: configForm.smtpPass,
          from: configForm.smtpFrom,
          fromName: configForm.smtpFromName,
        })
      });
      if (res.ok) {
        toast.success('SMTP连接测试成功！');
      } else {
        const data = await res.json();
        toast.error(data.error || 'SMTP连接测试失败');
      }
    } catch {
      toast.error('测试失败');
    }
    setSmtpTesting(false);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('请输入测试邮箱地址');
      return;
    }
    setSendingTestEmail(true);
    try {
      const res = await platformFetch('/config/smtp/send-test', {
        method: 'POST',
        body: JSON.stringify({ to: testEmail })
      });
      if (res.ok) {
        toast.success('测试邮件已发送，请查收！');
        setTestEmail('');
      } else {
        const data = await res.json();
        toast.error(data.error || '发送失败');
      }
    } catch {
      toast.error('发送失败');
    }
    setSendingTestEmail(false);
  };

  const handleSavePromptOptimizeConfig = async () => {
    // 校验 model 必须是合法 uuid（避免老字符串误写回）
    const model = (promptOptimizeForm.model ?? '').trim();
    if (promptOptimizeForm.isEnabled && model && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(model)) {
      toast.error('请重新选择 LLM 模型（当前值非合法模型 ID）');
      return;
    }
    try {
      await promptOptimizeAPI.updateConfig(promptOptimizeForm);
      setPromptOptimizeSaved(true);
      setTimeout(() => setPromptOptimizeSaved(false), 2000);
      loadData();
    } catch (error) {
      toast.error('保存失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  const orderedModels = useMemo(
    () => [...models].sort((a, b) => (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name)),
    [models]
  );

  const handleMoveModel = async (id: string, direction: 'up' | 'down' | 'top') => {
    const currentIndex = orderedModels.findIndex((model) => model.id === id);
    if (currentIndex === -1) return;

    const targetIndex =
      direction === 'top'
        ? 0
        : direction === 'up'
          ? currentIndex - 1
          : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= orderedModels.length || targetIndex === currentIndex) {
      return;
    }

    const reordered = [...orderedModels];
    const [movedModel] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, movedModel);
    const nextModels = reordered.map((model, index) => ({ ...model, sortOrder: (index + 1) * 10 }));

    const previous = models;
    setMovingModelId(id);
    setModels(nextModels);
    try {
      const res = await api.reorderModels(nextModels.map((model) => model.id));
      setModels(res.data);
    } catch (error) {
      setModels(previous);
      toast.error(error instanceof Error ? error.message : '排序更新失败');
    } finally {
      setMovingModelId(null);
    }
  };

  const handleDeleteModel = async (id: string) => {
    if (!confirm('确定要删除这个模型配置吗？')) return;
    try {
      const res = await platformFetch(`/models/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) loadData();
      else toast.error('删除失败');
    } catch {
      toast.error('删除失败');
    }
  };

  const handleDeleteFAQ = async (id: string) => {
    if (!confirm('确定要删除这个 FAQ 吗？')) return;
    try {
      await faqAPI.deleteFAQ(id);
      loadData();
    } catch {
      toast.error('删除失败');
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    if (!confirm('确定要删除这个公告吗？')) return;
    try {
      await announcementAPI.deleteAnnouncement(id);
      loadData();
    } catch {
      toast.error('删除失败');
    }
  };

  const handleToggleAnnouncement = async (id: string, isVisible: boolean) => {
    try {
      await announcementAPI.updateAnnouncement(id, { isVisible: !isVisible });
      loadData();
    } catch {
      toast.error('操作失败');
    }
  };

  const handleCreateCodes = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await platformFetch('/credits/codes', {
        method: 'POST',
        body: JSON.stringify({
          credits: createForm.credits,
          count: createForm.count,
          maxUses: createForm.maxUses,
          expiresAt: createForm.expiresAt || undefined,
          prefix: createForm.prefix,
        })
      });
      if (res.ok) {
        const data = await res.json();
        setCreatedCodes(data.codes);
        setCreateForm({ credits: 10, count: 1, maxUses: 1, expiresAt: '', prefix: '' });
        loadData();
      } else {
        const error = await res.json();
        toast.error(error.error || '创建失败');
      }
    } catch {
      toast.error('创建兑换码失败');
    }
  };

  const handleDisableCode = async (codeId: string) => {
    if (!confirm('确定要禁用该兑换码吗？')) return;
    try {
      const res = await platformFetch(`/credits/codes/${codeId}`, {
        method: 'DELETE',
      });
      if (res.ok) loadData();
      else toast.error('禁用失败');
    } catch {
      toast.error('禁用失败');
    }
  };

  const overviewSections: SidebarSection[] = [
    {
      items: [
        { key: 'dashboard', label: '仪表盘', icon: LayoutDashboard },
        { key: 'generationStats', label: '生成统计', icon: BarChart3 },
        { key: 'freeCreditStats', label: '非充值积分统计', icon: Gift },
        { key: 'userMilestones', label: '用户里程碑', icon: TrendingUp },
        { key: 'revenueMilestones', label: '收入里程碑', icon: DollarSign },
      ],
    },
  ];

  const userBusinessSections: SidebarSection[] = [
    {
      title: '用户与邀请',
      items: [
        { key: 'users', label: '用户管理', icon: Users },
        { key: 'blacklist', label: '风控黑名单', icon: Shield },
        { key: 'smsRecords', label: '短信记录', icon: MessageSquare },
        { key: 'invitations', label: '邀请记录', icon: Gift },
        { key: 'referralRebate', label: '推广返利', icon: HandCoins },
      ],
    },
    {
      title: '交易与积分',
      items: [
        { key: 'orders', label: '订单管理', icon: ShoppingCart },
        { key: 'orderStats', label: '订单统计', icon: TrendingUp },
        { key: 'paymentConfig', label: '支付配置', icon: CreditCard },
        { key: 'rechargePackages', label: '充值套餐', icon: Package },
        { key: 'redeemCodes', label: '兑换码管理', icon: Gift },
      ],
    },
  ];

  const contentPlatformSections: SidebarSection[] = [
    {
      title: '内容运营',
      items: [
        { key: 'faq', label: 'FAQ 管理', icon: HelpCircle },
        { key: 'announcements', label: '公告管理', icon: Megaphone },
        { key: 'templates', label: '模板管理', icon: FileText },
        { key: 'reportedWorks', label: '举报管理', icon: Flag },
      ],
    },
    {
      title: '平台与权限',
      items: [
        { key: 'settings', label: '系统配置', icon: Settings },
        { key: 'oauthConfig', label: 'OAuth 登录配置', icon: Shield },
      ],
    },
  ];

  const businessBaselineSections: SidebarSection[] = [
    {
      title: '图像服务',
      items: [
        { key: 'imageConfig', label: '图片配置', icon: Settings },
        { key: 'apiChannels', label: '图片渠道', icon: Key },
        { key: 'upstreamBalances', label: '上游余额', icon: Wallet },
        { key: 'imageProviders', label: '图片 Adapter', icon: Plug },
        { key: 'models', label: '图片模型', icon: ImageIcon },
        { key: 'images', label: '图片记录', icon: ImageIcon },
        { key: 'modelFallbackStats', label: '降级统计', icon: BarChart3 },
      ],
    },
    {
      title: '视频服务',
      items: [
        { key: 'videoChannels', label: '视频渠道', icon: Key },
        { key: 'videoProviders', label: '视频 Adapter', icon: Plug },
        { key: 'videoModels', label: '视频模型', icon: Film },
        { key: 'videoJobs', label: '视频任务', icon: History },
        { key: 'videoSystemConfig', label: '视频系统配置', icon: Settings },
      ],
    },
    {
      title: 'LLM 服务',
      items: [
        { key: 'llmPlatform', label: 'LLM 平台', icon: Bot },
        { key: 'promptOptimize', label: 'AI 提示词优化', icon: Sparkles },
        { key: 'llmTrafficPackages', label: 'LLM 流量设置', icon: Coins },
      ],
    },
  ];

  const infrastructureSections: SidebarSection[] = [
    {
      title: '存储管理',
      items: [
        { key: 'storageConfig', label: '存储配置', icon: HardDrive },
        { key: 'storageBucket', label: '存储桶管理', icon: HardDrive },
        { key: 'storageCleanup', label: '孤立文件清理', icon: HardDrive },
        { key: 'dbSchema', label: '数据库预览', icon: Database },
        { key: 'failureLogs', label: '统一失败日志', icon: History },
        { key: 'errorRules', label: '错误映射', icon: Wand2 },
      ],
    },
  ];

  const applicationBaselineSections: SidebarSection[] = [
    {
      title: '应用管理',
      items: [
        { key: 'applications', label: '应用管理', icon: LayoutGrid },
        { key: 'appRelease', label: 'App 发布', icon: Package },
      ],
    },
    {
      title: '短剧',
      items: [
        { key: 'directorDataHub', label: '数据中心', icon: FolderOpen },
        { key: 'directorAIConfig', label: '短剧 AI 配置', icon: Wand2 },
        { key: 'directorAngleTemplates', label: '视角模板', icon: Sparkles },
        { key: 'directorBasicConfig', label: '基础配置', icon: Settings },
      ],
    },
    {
      title: '画布',
      items: [
        { key: 'canvasProjects', label: '画布项目', icon: LayoutGrid },
        { key: 'canvasStats', label: '画布统计', icon: BarChart3 },
        { key: 'canvasAIConfig', label: '画布 AI 配置', icon: Settings },
        { key: 'canvasStyleCards', label: '风格卡片', icon: Palette },
      ],
    },
    {
      title: '电商复刻',
      items: [
        { key: 'ecommerceTasks', label: '复刻任务', icon: ShoppingBag },
        { key: 'ecommerceStats', label: '复刻统计', icon: BarChart3 },
      ],
    },
    {
      title: '电商套图',
      items: [
        { key: 'ecommerceSuiteConfig', label: '套图配置', icon: Settings },
        { key: 'ecommerceSuiteProjects', label: '套图项目', icon: ShoppingBag },
        { key: 'ecommerceSuiteStats', label: '套图统计', icon: BarChart3 },
        { key: 'ecommerceSuiteTemplates', label: '自定义模板', icon: Layers },
      ],
    },
    {
      title: 'PPT',
      items: [
        { key: 'pptProjects', label: 'PPT 项目', icon: Presentation },
      ],
    },
  ];

  const getActiveGroup = (): AdminGroup => {
    if (overviewSections.some(section => section.items.some(item => item.key === activeTab))) return 'overview';
    if (userBusinessSections.some(section => section.items.some(item => item.key === activeTab))) return 'userBusiness';
    if (contentPlatformSections.some(section => section.items.some(item => item.key === activeTab))) return 'contentPlatform';
    if (businessBaselineSections.some(section => section.items.some(item => item.key === activeTab))) return 'businessBaseline';
    if (infrastructureSections.some(section => section.items.some(item => item.key === activeTab))) return 'infrastructure';
    return 'applicationBaseline';
  };
  const [expandedGroup, setExpandedGroup] = useState<AdminGroup>(getActiveGroup);

  const tabTitles: Record<ActiveTab, string> = {
    dashboard: '仪表盘',
    generationStats: '生成统计',
    modelFallbackStats: '降级统计',
    freeCreditStats: '非充值积分统计',
    userMilestones: '用户里程碑',
    revenueMilestones: '收入里程碑',
    users: '用户管理',
    smsRecords: '短信记录',
    images: '图片记录',
    failureLogs: '统一失败日志',
    errorRules: '错误映射',
    imageConfig: '图片配置',
    storageCleanup: '孤立文件清理',
    appRelease: 'App 发布',
    storageBucket: '存储桶管理',
    invitations: '邀请记录',
    referralRebate: '推广返利',
    orders: '订单管理',
    orderStats: '订单统计',
    settings: '系统配置',
    applications: '应用管理',
    redeemCodes: '兑换码管理',
    apiChannels: 'API 渠道管理',
    upstreamBalances: '上游余额',
    imageProviders: '图片 Adapter 管理',
    models: '模型配置',
    faq: 'FAQ 管理',
    announcements: '公告管理',
    promptOptimize: 'AI提示词优化配置',
    templates: '模板管理',
    paymentConfig: '支付配置',
    rechargePackages: '充值套餐管理',
    llmTrafficPackages: 'LLM 流量包管理',
    reportedWorks: '举报管理',
    oauthConfig: 'OAuth 登录配置',
    videoChannels: '视频 API 渠道',
    videoProviders: '视频 Adapter 管理',
    videoModels: '视频模型配置',
    videoJobs: '视频任务记录',
    storageConfig: '存储配置',
    dbSchema: '数据库结构预览',
    videoSystemConfig: '视频系统配置',
    directorDataHub: '短剧管理 · 数据中心',
    directorAIConfig: '短剧管理 · AI 功能配置',
    directorAngleTemplates: '短剧管理 · 视角模板',
    directorBasicConfig: '短剧管理 · 基础配置',
    llmPlatform: 'LLM 平台',
    canvasProjects: '画布项目',
    canvasStats: '画布统计',
    canvasAIConfig: '画布 AI 配置',
    canvasStyleCards: '风格卡片管理',
    ecommerceTasks: '复刻任务',
    ecommerceStats: '复刻统计',
    ecommerceSuiteConfig: '电商套图配置',
    ecommerceSuiteProjects: '电商套图项目',
    ecommerceSuiteStats: '电商套图统计',
    ecommerceSuiteTemplates: '电商套图自定义模板',
    pptProjects: 'PPT 项目管理',
    blacklist: '风控黑名单',
  };

  return (
    <div className="min-h-screen bg-light-bg">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-64 bg-white border-r border-light-border shadow-lg z-50 flex flex-col transition-transform duration-300 lg:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 shrink-0">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-accent-blue rounded-xl flex items-center justify-center">
              <LayoutDashboard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-light-text">管理后台</h1>
              <p className="text-xs text-light-muted">Admin Panel</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <nav className="space-y-4">
            <SidebarGroup
              title="概览"
              icon={PieChart}
              sections={overviewSections}
              isOpen={expandedGroup === 'overview'}
              onToggle={() => setExpandedGroup('overview')}
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab);
                setIsMobileMenuOpen(false);
              }}
            />
            <SidebarGroup
              title="用户与商业"
              icon={Users}
              sections={userBusinessSections}
              isOpen={expandedGroup === 'userBusiness'}
              onToggle={() => setExpandedGroup('userBusiness')}
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab);
                setIsMobileMenuOpen(false);
              }}
            />
            <SidebarGroup
              title="内容与平台"
              icon={FolderOpen}
              sections={contentPlatformSections}
              isOpen={expandedGroup === 'contentPlatform'}
              onToggle={() => setExpandedGroup('contentPlatform')}
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab);
                setIsMobileMenuOpen(false);
              }}
            />
            <SidebarGroup
              title="业务基线"
              icon={ImageIcon}
              sections={businessBaselineSections}
              isOpen={expandedGroup === 'businessBaseline'}
              onToggle={() => setExpandedGroup('businessBaseline')}
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab);
                setIsMobileMenuOpen(false);
              }}
            />
            <SidebarGroup
              title="应用基线"
              icon={Clapperboard}
              sections={applicationBaselineSections}
              isOpen={expandedGroup === 'applicationBaseline'}
              onToggle={() => setExpandedGroup('applicationBaseline')}
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab);
                setIsMobileMenuOpen(false);
              }}
            />
            <SidebarGroup
              title="基础设施"
              icon={HardDrive}
              sections={infrastructureSections}
              isOpen={expandedGroup === 'infrastructure'}
              onToggle={() => setExpandedGroup('infrastructure')}
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab);
                setIsMobileMenuOpen(false);
              }}
            />
          </nav>
        </div>

        <div className="shrink-0 p-4 border-t border-light-border">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-light-text hover:bg-light-hover transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            返回首页
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 lg:mb-8">
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 hover:bg-light-hover rounded-xl transition-colors"
            >
              <Menu className="w-6 h-6 text-light-text" />
            </button>
            <h2 className="text-xl lg:text-2xl font-bold text-light-text">{tabTitles[activeTab]}</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-light-border">
              <User className="w-4 h-4 text-light-muted" />
              <span className="text-sm text-light-text truncate max-w-[150px]">{currentUser?.email}</span>
            </div>
          </div>
        </div>

        {/* Dashboard */}
        {activeTab === 'dashboard' && stats && (
          <DashboardTab stats={stats} />
        )}

        {/* Generation Stats */}
        {activeTab === 'generationStats' && (
          <GenerationStatsTab />
        )}

        {/* Model Fallback Stats */}
        {activeTab === 'modelFallbackStats' && (
          <ModelFallbackStatsTab />
        )}

        {activeTab === 'freeCreditStats' && (
          <FreeCreditStatsTab />
        )}

        {activeTab === 'userMilestones' && (
          <UserMilestonesTab />
        )}

        {activeTab === 'revenueMilestones' && (
          <RevenueMilestonesTab />
        )}

        {/* Users */}
        {activeTab === 'users' && (
          <UsersTab
            users={users}
              pagination={usersPagination}
              loading={loading}
              searchQuery={usersSearchQuery}
              onSearchChange={handleUserSearchChange}
            multiIpOnly={usersMultiIpOnly}
              onMultiIpOnlyChange={handleUsersMultiIpOnlyChange}
            minSameIp={usersMinSameIp}
              onMinSameIpChange={handleUsersMinSameIpChange}
            multiLoginIpOnly={usersMultiLoginIpOnly}
              onMultiLoginIpOnlyChange={handleUsersMultiLoginIpOnlyChange}
            minSameLoginIp={usersMinSameLoginIp}
              onMinSameLoginIpChange={handleUsersMinSameLoginIpChange}
            sameFingerprintOnly={usersSameFingerprintOnly}
              onSameFingerprintOnlyChange={handleUsersSameFingerprintOnlyChange}
            onAdjustCredits={setCreditAdjustUser}
            onViewCreditHistory={handleViewCreditHistory}
            onViewRechargeOrders={handleViewUserRechargeOrders}
            onToggleUser={handleToggleUser}
            onEditUser={setEditingUser}
            onResetPassword={setResetPasswordUser}
            onImpersonateUser={handleImpersonateUser}
              onPageChange={handleUserPageChange}
          />
        )}

        {/* 风控黑名单 */}
        {activeTab === 'blacklist' && (
          <BlacklistTab />
        )}

        {/* SMS Records */}
        {activeTab === 'smsRecords' && (
          <SmsRecordsTab
            records={smsRecords}
            pagination={smsRecordsPagination}
            loading={smsRecordsLoading}
            onRefresh={() => loadSmsRecords(smsRecordsPagination.page, smsFilters)}
            onPageChange={handleSmsPageChange}
            onSearch={handleSmsSearch}
            onQueryRecord={handleQuerySmsRecord}
          />
        )}

        {/* Images */}
        {activeTab === 'images' && (
          <ImagesTab
            images={images}
            previewImage={previewImage}
            pagination={imagePagination}
            loading={loading}
            filters={imageFilters}
            onPreview={setPreviewImage}
            onClosePreview={() => setPreviewImage(null)}
            onPageChange={handleImagePageChange}
            onDeleteImages={handleDeleteImages}
            onFilterChange={handleImageFilterChange}
            onApplyFilters={handleApplyImageFilters}
            onResetFilters={handleResetImageFilters}
          />
        )}

        {/* Invitations */}
        {activeTab === 'invitations' && (
          <InvitationsTab
            invitations={adminInvitations}
            loading={loading}
            onRefresh={loadData}
          />
        )}

        {activeTab === 'referralRebate' && <ReferralRebateTab />}

        {/* Image Config */}
        {activeTab === 'imageConfig' && <ImageConfigTab />}

        {/* Unified Failure Logs */}
        {activeTab === 'failureLogs' && <FailureLogsTab />}

        {activeTab === 'errorRules' && <ErrorRulesTab />}

        {activeTab === 'storageCleanup' && (
          <StorageCleanupTab />
        )}

        {activeTab === 'appRelease' && (
          <AppReleaseTab />
        )}

        {activeTab === 'storageBucket' && (
          <StorageBucketTab />
        )}

        {activeTab === 'dbSchema' && (
          <DatabaseSchemaTab />
        )}

        {/* Orders */}
        {activeTab === 'orders' && (
          <OrdersTab
            orders={orders}
            filters={orderSearchParams}
            pagination={orderPagination}
            stats={orderStats}
            loading={loading}
            onRefresh={() => loadOrders(orderPagination.page, orderSearchParams)}
            onPageChange={handleOrderPageChange}
            onSearch={handleOrderSearch}
            onCompleteOrder={handleCompleteOrder}
            onRefundOrder={handleRefundOrder}
          />
        )}

        {/* Order Stats */}
        {activeTab === 'orderStats' && (
          <OrderStatsTab />
        )}

        {/* Redeem Codes */}
        {activeTab === 'redeemCodes' && (
          <RedeemCodesTab
            redeemCodes={redeemCodes}
            redemptions={redemptions}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onCreateCodes={handleCreateCodes}
            onDisableCode={handleDisableCode}
            createForm={createForm}
            onCreateFormChange={setCreateForm}
            createdCodes={createdCodes}
            onClearCreatedCodes={() => setCreatedCodes([])}
          />
        )}

        {/* Templates */}
        {activeTab === 'templates' && (
          <TemplatesTab
            templates={templates}
            onLoadData={loadData}
          />
        )}

        {/* Settings */}
        {activeTab === 'settings' && (
          <SettingsTab
            config={config}
            configForm={configForm}
            onConfigChange={setConfigForm}
            onSave={handleSaveConfig}
            onTestSMTP={handleTestSMTP}
            onSendTestEmail={handleSendTestEmail}
            configSaved={configSaved}
            smtpTesting={smtpTesting}
            sendingTestEmail={sendingTestEmail}
            testEmail={testEmail}
            onTestEmailChange={setTestEmail}
            models={models}
          />
        )}

        {/* Applications */}
        {activeTab === 'applications' && (
          <ApplicationsTab
            applications={applications}
            loading={applicationsLoading}
            onApplicationUpdated={(updated) => {
              setApplications((current) =>
                current.map((application) => (application.appId === updated.appId ? updated : application))
              );
            }}
            onRefresh={loadData}
          />
        )}

        {/* API Channels */}
        {activeTab === 'apiChannels' && (
          <ApiChannelsTab
            channels={apiChannels}
            healthMap={channelHealth}
            onRefresh={loadData}
            onEdit={(channel) => {
              setEditingChannel(channel);
              setShowChannelForm(true);
            }}
            onCreate={() => {
              setEditingChannel(null);
              setShowChannelForm(true);
            }}
            onClone={async (channel) => {
              try {
                // 获取原渠道的完整 API Key
                const { apiKey } = await apiChannelAPI.getChannelKey(channel.id);
                setEditingChannel({
                  ...channel,
                  id: '',
                  name: `${channel.name} (复制)`,
                  apiKey,
                });
                setShowChannelForm(true);
              } catch (error) {
                toast.error('获取原渠道 API Key 失败，无法克隆');
              }
            }}
          />
        )}

        {/* Image Providers */}
        {activeTab === 'upstreamBalances' && <UpstreamBalancesTab />}
        {activeTab === 'imageProviders' && <ImageProvidersTab />}

        {/* Models */}
        {activeTab === 'models' && (
          <ModelsTab
            models={orderedModels}
            onMove={handleMoveModel}
            movingId={movingModelId}
            onAdd={() => {
              setEditingModel(null);
              setShowModelForm(true);
            }}
            onEdit={(model) => {
              setEditingModel(model);
              setShowModelForm(true);
            }}
            onConfigureRouting={(model) => {
              setRoutingModel(model);
              setShowRoutingForm(true);
            }}
            onDelete={handleDeleteModel}
          />
        )}

        {/* FAQ */}
        {activeTab === 'faq' && (
          <FAQTab
            faqs={faqs}
            onAdd={() => {
              setEditingFAQ(null);
              setShowFAQForm(true);
            }}
            onEdit={(faq) => {
              setEditingFAQ(faq);
              setShowFAQForm(true);
            }}
            onDelete={handleDeleteFAQ}
          />
        )}

        {/* Announcements */}
        {activeTab === 'announcements' && (
          <AnnouncementsTab
            announcements={announcements}
            onAdd={() => {
              setEditingAnnouncement(null);
              setShowAnnouncementForm(true);
            }}
            onEdit={(announcement) => {
              setEditingAnnouncement(announcement);
              setShowAnnouncementForm(true);
            }}
            onDelete={handleDeleteAnnouncement}
            onToggle={handleToggleAnnouncement}
          />
        )}

        {/* Prompt Optimize */}
        {activeTab === 'promptOptimize' && (
          <PromptOptimizeTab
            form={promptOptimizeForm}
            onFormChange={setPromptOptimizeForm}
            onSave={handleSavePromptOptimizeConfig}
            saved={promptOptimizeSaved}
          />
        )}

        {/* Payment Config */}
        {activeTab === 'paymentConfig' && <PaymentConfigTab />}

        {/* Recharge Packages */}
        {activeTab === 'rechargePackages' && <RechargePackagesTab />}

        {/* LLM Traffic Packages */}
        {activeTab === 'llmTrafficPackages' && <LlmTrafficPackagesTab />}

        {/* Reported Works */}
        {activeTab === 'reportedWorks' && <ReportedWorksTab />}

        {/* OAuth Config */}
        {activeTab === 'oauthConfig' && <OAuthConfigTab />}

        {/* Video Management */}
        {activeTab === 'videoChannels' && <VideoChannelsTab />}
        {activeTab === 'videoProviders' && <VideoProvidersTab />}
        {activeTab === 'videoModels' && <VideoModelsTab />}
        {activeTab === 'videoJobs' && <VideoJobsTab />}
        {activeTab === 'storageConfig' && <StorageConfigTab />}
        {activeTab === 'videoSystemConfig' && <VideoSystemConfigTab />}

        {/* 短剧管理（导演模式） */}
        {activeTab === 'directorDataHub' && <DirectorDataHubTab />}
        {activeTab === 'directorAIConfig' && <DirectorAIConfigTab />}
        {activeTab === 'directorAngleTemplates' && <DirectorAngleTemplatesHubTab />}
        {activeTab === 'directorBasicConfig' && <DirectorBasicConfigTab />}
        {activeTab === 'llmPlatform' && <LlmPlatformTab />}
        {activeTab === 'canvasProjects' && <CanvasProjectsTab />}

        {/* Canvas AI Config */}
        {activeTab === 'canvasAIConfig' && <CanvasAIConfigTab />}
        {activeTab === 'canvasStyleCards' && <CanvasStyleCardsTab />}
        {activeTab === 'canvasStats' && <CanvasStatsTab />}
        {activeTab === 'ecommerceTasks' && <EcommerceTasksTab />}
        {activeTab === 'ecommerceStats' && <EcommerceStatsTab />}
        {activeTab === 'ecommerceSuiteConfig' && <EcommerceSuiteConfigTab />}
        {activeTab === 'ecommerceSuiteProjects' && <EcommerceSuiteProjectsTab />}
        {activeTab === 'ecommerceSuiteStats' && <EcommerceSuiteStatsTab />}
        {activeTab === 'ecommerceSuiteTemplates' && <EcommerceSuiteTemplatesTab />}
        {activeTab === 'pptProjects' && <PptProjectsTab />}

        {/* Channel Edit Modal */}
        {showChannelForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-light-text">
                  {editingChannel
                    ? editingChannel.id
                      ? '编辑 API 渠道'
                      : '克隆 API 渠道'
                    : '添加 API 渠道'}
                </h3>
                <button
                  onClick={() => setShowChannelForm(false)}
                  className="p-2 hover:bg-light-hover rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-light-muted" />
                </button>
              </div>
              <AdminApiChannelForm
                channel={editingChannel}
                onClose={() => setShowChannelForm(false)}
                onSuccess={() => {
                  setShowChannelForm(false);
                  loadData();
                }}
              />
            </div>
          </div>
        )}

        {/* Model Edit Modal */}
        {showModelForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 py-6 backdrop-blur-sm">
            <div className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-white/60 bg-light-bg shadow-2xl">
              <div className="flex items-center justify-between border-b border-light-border bg-white px-6 py-5">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.16em] text-light-muted">Model Configuration</p>
                  <h3 className="mt-1 text-xl font-semibold text-light-text">
                    {editingModel ? '编辑模型' : '添加模型'}
                  </h3>
                </div>
                <button
                  onClick={() => setShowModelForm(false)}
                  className="rounded-xl p-2 transition-colors hover:bg-light-hover"
                >
                  <X className="w-5 h-5 text-light-muted" />
                </button>
              </div>
              <div className="min-h-0 flex-1 p-6">
                <AdminModelForm
                  model={editingModel}
                  onConfigureRouting={(model) => {
                    setRoutingModel(model);
                    setShowRoutingForm(true);
                  }}
                  onClose={() => setShowModelForm(false)}
                  onSuccess={() => {
                    setShowModelForm(false);
                    loadData();
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {showRoutingForm && routingModel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-2 py-3 backdrop-blur-sm sm:px-4 sm:py-6">
            <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-white/60 bg-light-bg shadow-2xl sm:max-h-[90vh] sm:rounded-3xl">
              <div className="flex items-start justify-between gap-3 border-b border-light-border bg-white px-4 py-3 sm:items-center sm:px-6 sm:py-5">
                <div className="min-w-0">
                  <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-light-muted sm:text-xs">Routing Configuration</p>
                  <h3 className="mt-1 break-words text-lg font-semibold text-light-text sm:text-xl">{routingModel.name} 路由配置</h3>
                </div>
                <button
                  onClick={() => setShowRoutingForm(false)}
                  className="shrink-0 rounded-xl p-2 transition-colors hover:bg-light-hover"
                >
                  <X className="w-5 h-5 text-light-muted" />
                </button>
              </div>
              <div className="min-h-0 flex-1 p-3 sm:p-6">
                <ModelRoutingForm
                  model={routingModel}
                  onClose={() => setShowRoutingForm(false)}
                  onSuccess={() => {
                    setShowRoutingForm(false);
                    loadData();
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* FAQ Edit Modal */}
        {showFAQForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-light-text">
                  {editingFAQ ? '编辑 FAQ' : '添加 FAQ'}
                </h3>
                <button
                  onClick={() => setShowFAQForm(false)}
                  className="p-2 hover:bg-light-hover rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-light-muted" />
                </button>
              </div>
              <AdminFAQForm
                faq={editingFAQ}
                onClose={() => setShowFAQForm(false)}
                onSuccess={() => {
                  setShowFAQForm(false);
                  loadData();
                }}
              />
            </div>
          </div>
        )}

        {/* Announcement Edit Modal */}
        {showAnnouncementForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-light-text">
                  {editingAnnouncement ? '编辑公告' : '添加公告'}
                </h3>
                <button
                  onClick={() => setShowAnnouncementForm(false)}
                  className="p-2 hover:bg-light-hover rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-light-muted" />
                </button>
              </div>
              <AdminAnnouncementForm
                announcement={editingAnnouncement}
                onClose={() => setShowAnnouncementForm(false)}
                onSuccess={() => {
                  setShowAnnouncementForm(false);
                  loadData();
                }}
              />
            </div>
          </div>
        )}

        {/* Credit History Modal */}
        {creditHistoryModal?.isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-light-border">
                <div>
                  <h3 className="text-lg font-semibold text-light-text">积分变动记录</h3>
                  <p className="text-sm text-light-muted mt-1">
                    {creditHistoryModal.userEmail} ({creditHistoryModal.userName})
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-light-muted">当前积分</p>
                    <p className="text-lg font-bold text-accent-blue">{creditHistoryModal.currentCredits}</p>
                  </div>
                  <button
                    onClick={() => setCreditHistoryModal(null)}
                    className="p-2 hover:bg-light-hover rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-light-muted" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto p-6">
                {creditHistoryModal.transactions.length === 0 ? (
                  <div className="text-center py-12 text-light-muted">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>暂无积分变动记录</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {creditHistoryModal.transactions.map((tx) => (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between p-4 bg-light-hover rounded-xl"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-light-text">
                            {tx.description || '积分变动'}
                          </p>
                          <p className="text-xs text-light-muted mt-1">
                            {formatBeijingDateTime(tx.createdAt)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className={`text-sm font-bold ${
                              tx.amount > 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {tx.amount > 0 ? '+' : ''}{tx.amount}
                          </span>
                          <span
                            className={`px-2 py-1 rounded-lg text-xs ${
                              tx.type === 'earn'
                                ? 'bg-green-100 text-green-600'
                                : 'bg-red-100 text-red-600'
                            }`}
                          >
                            {tx.type === 'earn' ? '获得' : '消费'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-light-border bg-light-hover rounded-b-2xl">
                <p className="text-xs text-light-muted text-center">
                  共 {creditHistoryModal.transactions.length} 条记录
                </p>
              </div>
            </div>
          </div>
        )}

        {/* User Edit Modal */}
        {creditAdjustUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-light-text">
                  调整用户积分
                </h3>
                <button
                  onClick={() => setCreditAdjustUser(null)}
                  className="p-2 hover:bg-light-hover rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-light-muted" />
                </button>
              </div>
              <UserCreditsForm
                user={creditAdjustUser}
                onClose={() => setCreditAdjustUser(null)}
                onSuccess={() => {
                  setCreditAdjustUser(null);
                  loadData();
                  toast.success('积分调整成功');
                }}
              />
            </div>
          </div>
        )}

        {/* User Edit Modal */}
        {editingUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-light-text">
                  编辑用户
                </h3>
                <button
                  onClick={() => setEditingUser(null)}
                  className="p-2 hover:bg-light-hover rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-light-muted" />
                </button>
              </div>
              <UserEditForm
                user={editingUser}
                onClose={() => setEditingUser(null)}
                onSuccess={() => {
                  setEditingUser(null);
                  loadData();
                }}
              />
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {resetPasswordUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-light-text">
                  重置密码
                </h3>
                <button
                  onClick={() => setResetPasswordUser(null)}
                  className="p-2 hover:bg-light-hover rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-light-muted" />
                </button>
              </div>
              <ResetPasswordForm
                user={resetPasswordUser}
                onClose={() => setResetPasswordUser(null)}
                onSuccess={() => {
                  setResetPasswordUser(null);
                  toast.success('密码重置成功');
                }}
              />
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
          </div>
        )}
      </main>
    </div>
  );
}

type AdminGroup = 'overview' | 'userBusiness' | 'contentPlatform' | 'businessBaseline' | 'infrastructure' | 'applicationBaseline';

type SidebarItem = {
  key: ActiveTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

type SidebarSection = {
  title?: string;
  items: SidebarItem[];
};

function SidebarGroup({
  title,
  icon: GroupIcon,
  sections,
  isOpen,
  onToggle,
  activeTab,
  onTabChange,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  sections: SidebarSection[];
  isOpen: boolean;
  onToggle: () => void;
  activeTab: ActiveTab;
  onTabChange: (tab: ActiveTab) => void;
}) {
  return (
    <div className="space-y-1">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
          isOpen
            ? 'bg-accent-blue/10 text-accent-blue'
            : 'text-light-text hover:bg-light-hover'
        }`}
      >
        <div className="flex items-center gap-3">
          <GroupIcon className="w-5 h-5" />
          <span className="font-medium">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>

      {isOpen && (
        <div className="ml-4 space-y-3">
          {sections.map((section, index) => (
            <SidebarSectionMenu
              key={section.title ?? index}
              section={section}
              activeTab={activeTab}
              onTabChange={onTabChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarSectionMenu({
  section,
  activeTab,
  onTabChange,
}: {
  section: SidebarSection;
  activeTab: ActiveTab;
  onTabChange: (tab: ActiveTab) => void;
}) {
  const items = section.items.map(({ key, label, icon: Icon }) => (
    <button
      key={key}
      onClick={() => onTabChange(key)}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm ${
        activeTab === key
          ? 'bg-accent-blue text-white shadow-md'
          : 'text-light-text hover:bg-light-hover'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  ));

  if (!section.title) {
    return <div className="ml-3 space-y-1">{items}</div>;
  }

  return (
    <div className="space-y-1">
      <div className="px-3 py-2 text-xs font-medium text-light-muted">{section.title}</div>
      <div className="ml-3 space-y-1">{items}</div>
    </div>
  );
}
